import { ParentState } from "@hopper-b2b/checkout";
import type * as H from "history";
import queryStringParser from "query-string";

import type { StateValue, send as xstateSend } from "xstate";
import config from "../../utils/config";
import { Event } from "./events";
import { CheckoutQueryParams, type FlightMachineContext } from "./types";

/**
 *  Stringifies first StateValue to add to url params
 * @example
 * stringifyParentStateValue({ passengerInfo: { picker: "idle" } });
 * returns "passengerInfo"
 */
export const stringifyParentStateValue = (state: StateValue) => {
  if (typeof state === "string") return state;
  else if (typeof state === "object") {
    const key = Object.keys(state)[0];
    return key;
  } else {
    return "";
  }
};

// List of states that should not be tracked in the query params
const untrackedStates = [
  ParentState.route,
  ParentState.loading,
  ParentState.passport,
  ParentState.cartQuote,
  ParentState.cartUpdate,
  ParentState.cartUpdateBeforeFulfill,
  ParentState.payment,
  ParentState.priceFreeze,
  ParentState.priceFreezeDiscount,
  ParentState.cartFulfill,
  ParentState.disruption,
];

// Map of states that should not transition when query params change
// Map: { state that should not transition => previous tracked state }
// Eg: if user is in "cartQuote" and hits back on their browser, the user should not be allowed to go back 1 step and
// instead stay in the current step till it resolves
const preventTransitionStates = {
  [ParentState.cartQuote]: ParentState.passengerInformation,
  [ParentState.cartUpdate]: ParentState.passengerInformation,
  [ParentState.cartUpdateBeforeFulfill]: ParentState.review,
  [ParentState.cartFulfill]: ParentState.review,
};

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: H.History
) => {
  const queryString = history?.location?.search || "";
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.[CheckoutQueryParams.checkoutState];

  const stringifiedNewState = stringifyParentStateValue(state);

  const shouldUpdateQuery = !untrackedStates.includes(
    stringifiedNewState as ParentState
  );

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL

  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify({
      ...parsedQuery,
      [CheckoutQueryParams.checkoutState]: stringifiedNewState,
    }),
    state: history.location.state,
  };

  if (!stateQueryParam && shouldUpdateQuery) {
    history.replace(historyData);
  } else if (
    stateQueryParam &&
    stringifiedNewState !== stateQueryParam &&
    shouldUpdateQuery
  ) {
    history.push(historyData);
  }
};

export const mapFlightStateToTransitionEvent = {
  [ParentState.contactInformation]: Event.GO_TO_CONTACT_INFORMATION,
  [ParentState.passengerInformation]: Event.GO_TO_PASSENGER_SELECT,
  [ParentState.review]: Event.GO_TO_REVIEW,
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
  [ParentState.seatSelection]: Event.GO_TO_SEAT_SELECTION,
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: H.History,
  send: typeof xstateSend
) => {
  const queryString = history.location.search;
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.[CheckoutQueryParams.checkoutState];

  const stringifiedNewState = stringifyParentStateValue(state);

  const preventTransition = !!preventTransitionStates[stringifiedNewState];

  const allowTransition =
    !preventTransition &&
    stateQueryParam &&
    !untrackedStates.includes(stateQueryParam as ParentState);

  const hasSucceededFulfillment =
    stringifiedNewState === ParentState.bookingConfirmation;

  if (
    stateQueryParam &&
    stringifiedNewState !== stateQueryParam &&
    allowTransition
  ) {
    if (hasSucceededFulfillment) {
      handleFulfillSuccess(history);
    } else {
      send({
        type: mapFlightStateToTransitionEvent[stateQueryParam as ParentState],
      });
    }
  } else if (
    preventTransition &&
    preventTransitionStates[stringifiedNewState] !== stateQueryParam
  ) {
    const historyData = {
      pathname: history.location.pathname,
      search: queryStringParser.stringify({
        ...parsedQuery,
        [CheckoutQueryParams.checkoutState]:
          preventTransitionStates[stringifiedNewState],
      }),
      state: history.location.state,
    };
    // Pushing the same stack that was removed when the user goes 1 step back in their browser
    history.push(historyData);
  }
};

export const handleFulfillSuccess = (history: H.History) => {
  window.parent.postMessage(
    {
      type: "FINISH_SESSION",
      payload: null,
    },
    config.UBER_URL
  );
};

export const validateContext = (ctx: FlightMachineContext) =>
  ((ctx.flightShop.shopPricingInfo.fare?.length || -1) > 0 &&
    ctx.flightSearch.departureDate &&
    ctx.flightSearch.destination &&
    ctx.flightSearch.origin &&
    ctx.flightShop.selectedTrip.outgoingFareId &&
    ctx.flightShop.selectedTrip.outgoingSliceId &&
    ctx.flightShop.selectedTrip.tripId &&
    Object.keys(ctx.flightShop.airports || {}).length > 0) ||
  //Handle price freeze exercise flow
  ctx.priceFreeze?.priceFreezeId;

export const getCheckoutStepOnQueryParam = () => {
  const queryString = window.location.search;
  const parsedQuery = new URLSearchParams(queryString);
  return parsedQuery.get(CheckoutQueryParams.checkoutState);
};

export const uberEmailRegex =
  // eslint-disable-next-line no-useless-escape
  /^(?!.*@(fake\.uberx|fake\.uber|uberx\.org\.cn|skipcaptcha\.uber\.com|deleted\.uber\.com|dost\.uber\.com|partners\.uber\.com|uberyr\.com))[a-zA-Z0-9]+(?:\.?[a-zA-Z0-9]+)*(?:[_\-]*(?:[a-zA-Z0-9]+\.[a-zA-Z0-9]+)*[a-zA-Z0-9]*)*@[a-zA-Z0-9]+(?:(?:\-*[a-zA-Z0-9]+)*\.[a-zA-Z0-9]+)+$/;

// List of unauthorized email domains
// - fake.uber.org
// - fake.uber.com
// - partners.uber.com
// - fake.uberx.org
// - fake.uberx.org
// - uberx.org.cn
// - uberyr.com
// - skipcaptcha.uber.com
// - deleted.uber.com
// - dost.uber.com
